import { useCallback, useRef, useState } from 'react';

/**
 * Provides an imperative `requestPin()` promise that resolves `true` once the
 * staff member has verified their PIN via the rendered `<PinDialog />`, or
 * `false` if they cancel. Render `<PinDialog open={isOpen} onVerified={handleVerified}
 * onCancel={handleCancel} />` wherever this hook is used.
 */
export function usePinVerification() {
  const [isOpen, setIsOpen] = useState(false);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const requestPin = useCallback((): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setIsOpen(true);
    });
  }, []);

  const handleVerified = useCallback(() => {
    resolverRef.current?.(true);
    resolverRef.current = null;
    setIsOpen(false);
  }, []);

  const handleCancel = useCallback(() => {
    resolverRef.current?.(false);
    resolverRef.current = null;
    setIsOpen(false);
  }, []);

  return { isOpen, requestPin, handleVerified, handleCancel };
}
